/** Маршрут звонка (`GET /api/calls/{id}/route`) и звонки номера (`GET /api/calls/phone/{номер}`).
 * Спека `docs/specs/2026-10-20-call-route-design.md`. */
import { apiFetch } from "@/lib/api";
import type { CallItem } from "@/lib/calls";

/** Вид оператора: ask — свободен и не взял; answered — взял; other — взял другой. */
export type RouteKind = "ask" | "unreached" | "busy" | "short" | "off" | "answered" | "other";

export type RouteOperator = {
  user_id: number;
  name: string;
  crm_id: number | null;
  kind: RouteKind;
  /** Сколько секунд очередь отдавала ему звонок. */
  offered: number;
  /** Из них — пока был на линии и не в своём звонке. */
  free: number;
  /** Сколько из них звонил его SIP-телефон. */
  rang: number;
  /** ring · offline · busy · decline · limit; пусто — телефона нет. */
  phone: string;
  /** Вкладка Bitrix показала карточку звонка. */
  tab: boolean;
  /** Что видела вкладка Bitrix с расширением (`call/browser.py`): дошёл ли вызов до экрана. */
  browser_text?: string;
};

export type RouteLeg = {
  user_id: number;
  name: string;
  /** phone — SIP-телефон (MicroSIP), browser — Bitrix в браузере. */
  device: "phone" | "browser";
  invited: string;
  ringing: string | null;
  connected: string | null;
  ended: string | null;
  rang: number;
  code: string;
  /** Вызов снял сценарий (конец круга или клиент ушёл), а не телефон. */
  by_queue: boolean;
  end: string;
};

export type RouteRound = {
  at: string;
  queue: string;
  legs: RouteLeg[];
  /** Что ответила вкладка Bitrix сотрудника: wait — показала звонок, busy — занята. */
  tabs: { user_id: number; name: string; command: string }[];
};

export type CallRoute = {
  call_id: number;
  incoming: boolean;
  start: string;
  line: string;
  log_url: string | null;
  log: boolean;
  error?: string;
  waited?: number;
  client_left?: string | null;
  queues?: string[];
  kind?: string;
  kinds?: Record<string, string>;
  /** Итог по одному сотруднику. */
  operator_kinds?: Record<string, string>;
  operators?: RouteOperator[];
  rounds?: RouteRound[];
  later?: { at: string; incoming: boolean; name: string }[];
};

export function loadCallRoute(id: number): Promise<CallRoute> {
  return apiFetch<CallRoute>(`/api/calls/${id}/route`);
}

export type PhoneCalls = { phone: string; limit: number; calls: CallItem[] };

export function loadPhoneCalls(phone: string): Promise<PhoneCalls> {
  return apiFetch<PhoneCalls>(`/api/calls/phone/${encodeURIComponent(phone)}`);
}

/** Любая запись номера → 11 цифр с 7 впереди; иначе пусто (как `route.номер` на сервере). */
export function normPhone(value: string): string {
  let d = value.replace(/\D/g, "");
  if (d.length === 10) d = `7${d}`;
  else if (d.length === 11 && d[0] === "8") d = `7${d.slice(1)}`;
  return d.length === 11 && d[0] === "7" ? d : "";
}

export function fmtPhone(d: string): string {
  return d.length === 11 ? `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}` : d;
}
